import { zodResolver } from '@hookform/resolvers/zod';
import { loginRequestSchema, type LoginRequest, type LoginResponse } from '@homecare/contracts';
import type { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Field, Notice } from '@homecare/ui';

import { ApiRequestError, request } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

import { AgencyPicker } from './agency-picker';
import { AUTH_LINK, AuthFrame } from './auth-frame';
import { asksForCode, signInNotice } from './sign-in-notices';

/** Six digits, as the API's own schema requires; checked here to say so in words. */
const SIX_DIGITS = /^\d{6}$/;

/**
 * Sign in (§7, §12).
 *
 * The schema is `loginRequestSchema`, imported from packages/contracts — the
 * same object the API validates the request with. Not a copy: a copy is a rule
 * that can be true on one side of the wire and false on the other, and the
 * side that drifts is always the client, because the client is the one nobody
 * tests against the database.
 *
 * ## Two steps, the second only when the API asks for it
 *
 * Owners, administrators and billers have an authenticator (§10.1), and for a
 * while this form had no field for its code — so the three roles with the most
 * access were the three that could not sign in. The field is not shown to
 * everyone: most people who sign in here have no authenticator, and a code box
 * they must leave blank is a question they cannot answer. The API answers the
 * password first; when it replies AUTH_MFA_CODE_REQUIRED, the password was
 * right, and the screen says so and asks for the code. Email and password are
 * kept and sent again with it — the API checks the password on every attempt,
 * which is what makes each guess at the code cost a password verification.
 *
 * Every refusal renders one statement and one next step. See
 * `sign-in-notices.ts` for why that needed saying.
 */
export function SignIn(): ReactNode {
  const navigate = useNavigate();
  const signIn = useSession((state) => state.signIn);

  // Two or more agencies share this address, so the password verified but the
  // tenant is still undecided (§7, amendment A2).
  const [selection, setSelection] = useState<Extract<
    LoginResponse,
    { outcome: 'agency_selection_required' }
  > | null>(null);

  const [failure, setFailure] = useState<unknown>(null);
  const [step, setStep] = useState<'credentials' | 'code'>('credentials');

  // Typed on the schema's INPUT, not its output. `audience` carries a
  // `.default()`, so Zod's input type has it optional and its output type has it
  // required — and a form holds what the person has typed so far, which is the
  // input. Getting this backwards makes every defaulted field look required to
  // the resolver.
  const form = useForm<z.input<typeof loginRequestSchema>, unknown, LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: '', password: '', audience: 'staff' },
    mode: 'onBlur',
  });

  useUnsavedChangesGuard(form.formState.isDirty && !form.formState.isSubmitSuccessful);

  if (selection) {
    return (
      <AgencyPicker
        selectionToken={selection.selectionToken}
        agencies={selection.agencies}
        onStartOver={() => {
          setSelection(null);
          setStep('credentials');
          form.reset();
        }}
      />
    );
  }

  const startOver = (): void => {
    setFailure(null);
    setStep('credentials');
    form.setValue('mfaCode', undefined);
    form.setValue('password', '');
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);

    if (step === 'code' && !SIX_DIGITS.test(values.mfaCode ?? '')) {
      form.setError('mfaCode', {
        message: 'Enter the 6-digit code from your authenticator app.',
      });
      return;
    }

    try {
      const result = await request<LoginResponse>('/v1/auth/login', {
        method: 'POST',
        body: step === 'code' ? values : { ...values, mfaCode: undefined },
      });

      if (result.outcome === 'agency_selection_required') {
        setSelection(result);
        return;
      }

      await signIn({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      await navigate({ to: '/' });
    } catch (error) {
      if (asksForCode(error)) {
        setStep('code');
        return;
      }
      // Anything other than a wrong code means the code step is no longer the
      // question — a password changed or an account locked in the meantime —
      // so the screen goes back to the step the notice is about.
      const wrongCode = error instanceof ApiRequestError && error.code === 'AUTH_MFA_INVALID';
      if (step === 'code' && !wrongCode) setStep('credentials');
      form.setValue('mfaCode', undefined);
      setFailure(error);
    }
  });

  const notice = failure ? signInNotice(failure) : null;

  return (
    // No visible heading and no lede. "Sign in" above a "Sign in" button is a
    // doubled word doing no work, and a sentence explaining what the product is
    // is being read by someone who opens it every morning. The mark on the left
    // says where you are; the form opens straight into the fields.
    <AuthFrame title="Sign in" titleHidden>
      <form noValidate onSubmit={onSubmit} className="grid gap-3">
        {notice && (
          <Notice tone="critical" title={notice.title}>
            <span data-next-step>{notice.nextStep}</span>
          </Notice>
        )}

        {step === 'credentials' ? (
          <>
            <Field
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />

            <Field
              label="Password"
              type="password"
              autoComplete="current-password"
              error={form.formState.errors.password?.message}
              {...form.register('password')}
            />

            {/* Above the button and right aligned, as the reference has it. Up here
                it reads as part of the password field's business — "not this one?"
                — rather than as an afterthought stranded under the action. */}
            <p className="text-right">
              <a href="/forgot-password" className={`${AUTH_LINK} text-sm`}>
                Forgot your password?
              </a>
            </p>

            <Button type="submit" className="w-full" pending={form.formState.isSubmitting}>
              Sign in
            </Button>
          </>
        ) : (
          <>
            {/* Not a notice: nothing went wrong. The password was right, and
                this is the second half of signing in. */}
            <div className="grid gap-1" role="status">
              <p className="text-ink text-md">Your password is right.</p>
              <p className="text-ink-2 text-sm">
                Enter the 6-digit code from your authenticator app for{' '}
                <span className="text-ink font-medium">{form.getValues('email')}</span>.
              </p>
            </div>

            <Field
              label="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              error={form.formState.errors.mfaCode?.message}
              {...form.register('mfaCode', {
                setValueAs: (value: string) => (value === '' ? undefined : value.trim()),
              })}
            />

            <Button type="submit" className="w-full" pending={form.formState.isSubmitting}>
              Finish signing in
            </Button>

            <p className="text-center">
              <button type="button" className={`${AUTH_LINK} text-sm`} onClick={startOver}>
                Sign in as someone else
              </button>
            </p>
          </>
        )}
      </form>

      {/*
        No "Don't have an account? Sign up".

        The reference carries one and we must not. Agencies are provisioned by
        us and their staff are invited by an administrator; there is no public
        registration, so the link would lead nowhere and the sentence would be
        telling an agency owner something untrue about how they get an account.
      */}
    </AuthFrame>
  );
}
